import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { 
  accessLocalStorage, 
  createOutboundConfig, 
  exportSelectAllTrunksWithInterconnection,
  exportSelectAllTrunks,
  fetchEndpoints
} from '../../../services';
import { scopeByTenant } from '../../../services/tenantScope';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './OutboundCallsForm.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faPhone, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

function OutboundCallsCreate() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsChangeFormElements, toggleIsHidden } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [userTenant, setUserTenant] = useState('');

  // Estados do formulário
  const [values, setValues] = useState({
    tenant: '',
    callType: 'local',
    mask: '',
    trunkName: '',
    ispCode: '',
    digitRemove: 0,
    timeout: 30,
    description: '',
    active: true
  });

  const [errors, setErrors] = useState({});

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });

  const history = useHistory();

  // Tipos de chamadas disponíveis
  const callTypes = [
    { value: 'support', label: 'Suporte', description: 'Chamadas de suporte técnico' },
    { value: 'emergency', label: 'Emergência', description: 'Chamadas de emergência (190, 192, 193)' },
    { value: 'freecall', label: 'Ligação Gratuita', description: 'Chamadas 0800' },
    { value: 'local', label: 'Local', description: 'Chamadas locais' },
    { value: 'mobile', label: 'Celular', description: 'Chamadas para celular local' },
    { value: 'ddd', label: 'DDD', description: 'Chamadas interurbanas' },
    { value: 'mobileDdd', label: 'Celular DDD', description: 'Chamadas para celular interurbano' },
    { value: 'international', label: 'Internacional', description: 'Chamadas internacionais' }
  ];

  const getTrunks = async () => {
    try {
      const trunksList = await exportSelectAllTrunksWithInterconnection();
      
      // Se a nova função falhar, usar a função tradicional
      let finalTrunksList = trunksList;
      
      // Verificar se é um array válido
      if (!Array.isArray(trunksList)) {
        console.warn('Função com interconexão falhou, usando função tradicional');
        finalTrunksList = await exportSelectAllTrunks();
      }
      
      if (!Array.isArray(finalTrunksList)) {
        console.error('Resposta da API não é um array:', finalTrunksList);
        return [];
      }

      const endpointsAstFetched = await fetchEndpoints();
      
      const trunksStatusAst = Array.isArray(endpointsAstFetched) ? endpointsAstFetched.reduce((acc, trunk) => {
        if (isNaN(trunk.resource)) {
          acc[trunk.resource] = trunk.state;
        }
        return acc;
      }, {}) : {};

      const trunksStatus = finalTrunksList.map((trunk) => { 
        return { 
          ...trunk, 
          state: trunksStatusAst[trunk.name] || 'offline' 
        } 
      });

      return trunksStatus;
    } catch (error) {
      console.error('Erro ao buscar trunks:', error);
      return [];
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = await accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    
    const { user: { role, idtenant } } = dataUser;
    
    // Definir tenant padrão
    setUserTenant(idtenant);
    setValues(prev => ({
      ...prev,
      tenant: idtenant
    }));

    // Buscar troncos usando a mesma lógica da TrunksList
    let trunksFetched = await getTrunks();
    
    // Garantir que trunksFetched é um array
    if (!Array.isArray(trunksFetched)) {
      trunksFetched = [];
    }
    
    // Filtrar por tenant se não for vgs
    trunksFetched = scopeByTenant(trunksFetched, (trunk) => trunk && trunk.tenant, idtenant);
    setAvailableTrunks(trunksFetched);
    
    setLoading(false);
  }, [history]);

  const fetchAvailableTrunks = async (tenant) => {
    // Esta função não é mais necessária, pois a lógica foi movida para validateUserLogged
    // Mantendo para compatibilidade, mas não faz nada
    return;
  };

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleInputChange = (field, value) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!values.tenant.trim()) {
      newErrors.tenant = 'Tenant é obrigatório';
    }

    if (!values.callType) {
      newErrors.callType = 'Tipo de chamada é obrigatório';
    }

    if (!values.mask.trim()) {
      newErrors.mask = 'Máscara é obrigatória';
    }

    if (!values.trunkName.trim()) {
      newErrors.trunkName = 'Tronco é obrigatório';
    }

    if (values.digitRemove < 0) {
      newErrors.digitRemove = 'Dígitos a remover deve ser maior ou igual a 0';
    }

    if (values.timeout < 1 || values.timeout > 300) {
      newErrors.timeout = 'Timeout deve estar entre 1 e 300 segundos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const dataToSend = {
        tenant: values.tenant,
        callType: values.callType,
        mask: values.mask,
        trunkName: values.trunkName,
        ispCode: values.ispCode || null,
        digitRemove: parseInt(values.digitRemove),
        timeout: parseInt(values.timeout),
        description: values.description || null,
        active: values.active
      };

      console.log('Dados enviados para o backend:', dataToSend);

      const response = await createOutboundConfig(dataToSend);
      
      console.log('Resposta do backend:', response);

      if (response && response.success) {
        showAlert('success', 'Configuração criada com sucesso!');
        setTimeout(() => {
          history.push('/config/chamadas/saida');
        }, 2000);
      } else {
        const errorMessage = response.message || response.error || 'Erro ao criar configuração';
        console.log('Erro ao criar configuração:', response);
        console.log('Detalhes do erro:', errorMessage);
        showAlert('danger', 'Erro ao criar configuração: ' + errorMessage);
      }
    } catch (error) {
      console.error('Erro ao criar configuração:', error);
      showAlert('danger', 'Erro ao criar configuração: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    // Scroll para o topo da página para mostrar o alert
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleCancel = () => {
    toggleIsChangeFormElements();
    toggleIsHidden();
    history.push('/config/chamadas/saida');
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="outbound-form-container">
      <div className="outbound-form-header">
        <div className="outbound-form-title-wrapper">
          <svg className="outbound-form-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <div>
            <h1 className="outbound-form-title">Nova Configuração de Chamada de Saída</h1>
            <p className="outbound-form-subtitle">Criar nova rota de chamadas</p>
          </div>
        </div>
        <button className="btn-back" onClick={handleCancel}>
          <FontAwesomeIcon icon={faTimes} />
          Voltar
        </button>
      </div>

      {/* Alert */}
      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
              <button className="alert-close" onClick={() => setAlert({ ...alert, show: false })}>×</button>
            </div>
            <div className="alert-body">{alert.message}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-section-card">
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Cliente *</label>
              <input
                className={`form-input ${errors.tenant ? 'is-danger' : ''}`}
                type="text"
                value={values.tenant}
                onChange={(e) => handleInputChange('tenant', e.target.value)}
                placeholder="Tenant"
                readOnly
                disabled
              />
              {errors.tenant && (
                <p className="form-help is-danger">{errors.tenant}</p>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">Tipo de Chamada *</label>
              <select
                className={`form-select ${errors.callType ? 'is-danger' : ''}`}
                value={values.callType}
                onChange={(e) => handleInputChange('callType', e.target.value)}
              >
                {callTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.callType && (
                <p className="form-help is-danger">{errors.callType}</p>
              )}
              {values.callType && (
                <p className="form-help is-info">
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '5px' }} />
                  {callTypes.find(t => t.value === values.callType)?.description}
                </p>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">Máscara *</label>
              <input
                className={`form-input ${errors.mask ? 'is-danger' : ''}`}
                type="text"
                value={values.mask}
                onChange={(e) => handleInputChange('mask', e.target.value)}
                placeholder="Ex: 9NXXXXXXX, 0800XXXXXXX"
              />
              {errors.mask && (
                <p className="form-help is-danger">{errors.mask}</p>
              )}
              <p className="form-help">
                Use N (2-9), X (0-9), Z (1-9) para padrões. 
                Underscore (_) será adicionado automaticamente quando necessário.
              </p>
            </div>

            <div className="form-field">
              <label className="form-label">Tronco *</label>
              <select
                className={`form-select ${errors.trunkName ? 'is-danger' : ''}`}
                value={values.trunkName}
                onChange={(e) => handleInputChange('trunkName', e.target.value)}
              >
                <option value="">Selecione um tronco</option>
                {availableTrunks.map((trunk) => (
                  <option key={trunk.name} value={trunk.name}>
                    {trunk.name} - {trunk.serverIP || trunk.server_uri || 'N/A'} ({trunk.state === 'online' ? '🟢 Online' : '🔴 Offline'})
                  </option>
                ))}
              </select>
              {errors.trunkName && (
                <p className="form-help is-danger">{errors.trunkName}</p>
              )}
              {availableTrunks.length === 0 && (
                <p className="form-help is-warning">
                  Nenhum tronco disponível para este tenant
                </p>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">Código Operadora</label>
              <input
                className="form-input"
                type="text"
                value={values.ispCode}
                onChange={(e) => handleInputChange('ispCode', e.target.value)}
                placeholder="Ex: 21 (opcional)"
              />
              <p className="form-help">
                Código da operadora para chamadas DDD/internacionais (opcional)
              </p>
            </div>

            <div className="form-field">
              <label className="form-label">Dígitos a Remover</label>
              <input
                className={`form-input ${errors.digitRemove ? 'is-danger' : ''}`}
                type="number"
                min="0"
                max="10"
                value={values.digitRemove}
                onChange={(e) => handleInputChange('digitRemove', e.target.value)}
              />
              {errors.digitRemove && (
                <p className="form-help is-danger">{errors.digitRemove}</p>
              )}
              <p className="form-help">
                Número de dígitos a remover do início do número discado
              </p>
            </div>

            <div className="form-field">
              <label className="form-label">Tempo limite da chamada (segundos) *</label>
              <input
                className={`form-input ${errors.timeout ? 'is-danger' : ''}`}
                type="number"
                min="1"
                max="300"
                value={values.timeout}
                onChange={(e) => handleInputChange('timeout', e.target.value)}
              />
              {errors.timeout && (
                <p className="form-help is-danger">{errors.timeout}</p>
              )}
              <p className="form-help">
                Tempo limite para a chamada (1-300 segundos)
              </p>
            </div>

            <div className="form-field">
              <div className="form-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="active-checkbox"
                  checked={values.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                />
                <label htmlFor="active-checkbox">Configuração Ativa</label>
              </div>
              <p className="form-help">
                Desmarque para criar a configuração inativa
              </p>
            </div>

            <div className="form-field form-field-full">
              <label className="form-label">Descrição</label>
              <textarea
                className="form-textarea"
                rows="3"
                value={values.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Descrição opcional da configuração"
              />
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={handleCancel}
              disabled={saving}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className={`btn-submit ${saving ? 'is-loading' : ''}`}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} />
              Criar Configuração
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default OutboundCallsCreate;
